import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { dayjs, Dayjs } from 'lib';
import { getCssVar } from 'utils';
import { MODEL_TYPE } from 'config/constants';
import { PlotView } from 'queries/scalings/contract';
import { means, ResponseItem } from './means';

type DataItem = {
  x: number;
  y: number;
  opacity: number;
  time: string;
};

interface HoverNode extends DataItem {
  color: string;
  itens?: number;
}

interface FilterdDataItem extends ResponseItem {
  opacity: number;
  color: string;
}

interface KData {
  status: string;
  itens: FilterdDataItem[];
}
interface HighlightArea {
  bottom?: number | undefined;
  left?: number | undefined;
  right?: number | undefined;
  top?: number | undefined;
}

const legend = ['learned', 'normal', 'warning', 'alarming'];

const usePlotScaling = ({
  data,
  error,
  isLoading,
  period,
  modelType,
  modelDate,
}: {
  data?: PlotView;
  error?: AxiosError<unknown, any> | null;
  isLoading?: boolean;
  period: [Dayjs | string | null, Dayjs | string | null] | null;
  modelName: string;
  modelType: string;
  modelDate: string;
}) => {
  const [hoveredNode, setHoveredNode] = useState<HoverNode | undefined>(undefined);
  const [dataPoints, setDataPoints] = useState<
    { itens: FilterdDataItem[]; status: string }[] | undefined
  >(undefined);
  const [dataFiltered, setDataFiltered] = useState<
    { itens: FilterdDataItem[]; status: string }[] | undefined
  >(undefined);
  const [filters, setFilters] = useState<string[]>([]);
  const [axes, setAxes] = useState<{ x: [number, number]; y: [number, number] } | undefined>(
    undefined,
  );

  const [highlight, setHighlight] = useState<HighlightArea | null>(null);
  const [dragging, setDragging] = useState<boolean>(false);
  const [kData, setKData] = useState<KData[] | undefined>(undefined);
  const [curvePoints, setCurvePoints] = useState<
    { name: string; data: { x: number; y: number }[] }[]
  >([]);
  const [showObservationMessage, setShowObservationMessage] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ startDate: period?.[0], endDate: period?.[1] });

  const getPeriodDates = () => {
    const modelDateDayjs = dayjs.utc(modelDate).local();
    const startDate = period?.[0];
    const endDate = period?.[1];
    setShowObservationMessage(false);
    setNewPeriod({ startDate, endDate });

    if (
      (modelDateDayjs.isAfter(startDate, 'day') && modelDateDayjs.isBefore(endDate, 'day')) ||
      modelDateDayjs.isAfter(endDate, 'day')
    ) {
      setShowObservationMessage(true);
    }
  };

  useEffect(() => {
    getPeriodDates();
  }, [period?.[0], period?.[1]]);

  useEffect(() => {
    if (data) {
      const xValues: number[] = [];
      const yValues: number[] = [];
      const blocking: {
        [key: string]: {
          itens: HoverNode[];
          means: number[][];
        };
      } = {
        alarming: { itens: [], means: [] },
        warning: { itens: [], means: [] },
        normal: { itens: [], means: [] },
      };

      const defaultData: {
        status: string;
        itens: HoverNode[];
      }[] = [];

      data.risk_score_data.forEach((item) => {
        const { value, time, x, y } = item;
        xValues.push(x);
        yValues.push(y);

        let key = 'normal';
        let color = '#63DC76';
        if (value > 0.75) {
          key = 'alarming';
          color = '#FF5454';
        } else if (value > 0.5) {
          key = 'warning';
          color = '#FFB054';
        }

        blocking[key].means.push([x, y]);
        blocking[key].itens.push({
          x,
          y,
          opacity: 0.7,
          time: dayjs(time).format('DD/MM/YYYY HH:mm:ss'),
          color,
        });
      });

      Object.keys(blocking).forEach((i) => {
        defaultData.push({
          status: i,
          itens: blocking[i].itens,
        });
      });

      setDataPoints(defaultData);

      setFilters(legend);

      const calculate = async () => {
        const result: KData[] = [];
        Object.keys(blocking).forEach(async (i) => {
          const sizeArr = blocking[i].means.length;
          if (sizeArr > 0) {
            const diffInDays = dayjs(newPeriod.endDate)?.diff(
              newPeriod.startDate,
              'days',
            ) as number;
            const k: ResponseItem[] =
              diffInDays <= 180 ? blocking[i].itens : await means(blocking[i].means, 10);

            let color = '#63DC76';
            if (i === 'alarming') {
              color = '#FF5454';
            }
            if (i === 'warning') {
              color = '#FFB054';
            }
            result.push({
              status: i,
              itens: k
                .filter((item) => item.itens === undefined || (item.itens && item.itens > 0))
                .map((item) => ({
                  ...item,
                  opacity: 0.7,
                  color,
                })),
            });
          }
        });

        setKData(result);
        setDataFiltered(result);
      };

      calculate();

      if (MODEL_TYPE.SCALING_OPERATION_POINT === modelType) {
        if (data.scaling_data.response_data.curve_points) {
          const result: { name: string; data: { x: number; y: number }[] }[] = [];
          const { x, y } = data.scaling_data.response_data.curve_points;
          x.forEach((itemX) => {
            xValues.push(itemX);
          });
          Object.keys(y).forEach((line) => {
            result.push({
              name: line,
              data: y[line].map((item, i) => {
                yValues.push(item);
                return {
                  y: item,
                  x: x[i],
                };
              }),
            });
          });
          setCurvePoints(result);
        }
      } else {
        data.scaling_data.response_data.grid_points?.forEach((item) => {
          xValues.push(item.x);
          yValues.push(item.y);
        });
      }

      const minX = Math.floor(Math.min(...xValues));
      const maxX = Math.ceil(Math.max(...xValues));
      const minY = Math.floor(Math.min(...yValues));
      const maxY = Math.ceil(Math.max(...yValues));

      setAxes({ x: [minX, maxX], y: [minY, maxY] });
    }
  }, [data]);

  const clickFilter = (status: string) => {
    let filter = [...filters];
    const index = filter.indexOf(status);

    if (dataPoints) {
      if (filter.length === 4) {
        filter = [status];
      } else if (filter.length === 1 && index === 0) {
        filter = legend;
      } else if (index > -1) {
        filter.splice(index, 1);
      } else {
        filter.push(status);
      }
    }
    setFilters(filter);
  };

  const highlightBrushStart = (area: HighlightArea | null) => {
    setDragging(false);
    if (area) {
      setHighlight(area);
    } else {
      setHighlight(null);
    }
  };

  useEffect(() => {
    if (dataPoints && kData) {
      if (highlight) {
        setDataFiltered(dataPoints);
      } else {
        setDataFiltered(kData);
      }
    }
  }, [highlight]);

  const getColorByStatus = (status: string) => {
    switch (status) {
      case 'normal':
        return '#63DC76';
      case 'alarming':
        return getCssVar('--red-1');
      case 'warning':
        return '#FFB054';
      default:
        return '#333';
    }
  };

  const getOperationColor = (name: string) => {
    switch (name) {
      case '15.0% above':
      case '15.0% below':
        return getCssVar('--red-2');
      case 'Ideal':
        return getCssVar('--green-1');
      default:
        return getCssVar('--gray-5');
    }
  };

  const dotsSeries = () => {
    if (dataFiltered) {
      return dataFiltered?.map((data) => {
        return {
          name: data.status,
          type: 'scatter',
          color: getColorByStatus(data.status),
          emphasis: {
            focus: 'series',
          },
          data: data.itens.map((item) => {
            const { x, y, time } = item;

            return [x, y, time];
          }),
        };
      });
    }
    return [];
  };

  const learnedRegion = () => {
    if (data && data.scaling_data.response_data && filters.indexOf('learned') > -1) {
      const dataPoints = data.scaling_data.response_data.grid_points?.map((point) => {
        const { x, y } = point;
        return [x, y];
      });

      if (dataPoints) {
        return [
          {
            type: 'line',
            smooth: true,
            showSymbol: false,
            color: '#999999',
            data: [
              ...(dataPoints as number[][]),
              [
                data.scaling_data.response_data.grid_points?.[0].x,
                data.scaling_data.response_data.grid_points?.[0].y,
              ],
            ],
            lineStyle: {
              normal: {
                width: 1,
              },
            },
          },
        ];
      }
    }
    return [];
  };

  const operationLines = () => {
    if (curvePoints && curvePoints.length) {
      return curvePoints.map((point) => ({
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: getOperationColor(point.name),
        data: point.data.map((point) => {
          const { x, y } = point;
          return [x, y];
        }),
        lineStyle: {
          normal: {
            width: 1,
          },
        },
      }));
    }
    return [];
  };

  return {
    dataPoints,
    dragging,
    isLoading,
    error: error as AxiosError,
    highlight,
    axes,
    data,
    dataFiltered,
    hoveredNode,
    filters,
    setDragging,
    highlightBrushStart,
    clickFilter,
    setHoveredNode,
    curvePoints,
    showObservationMessage,
    dotsSeries,
    learnedRegion,
    operationLines,
    getColorByStatus,
    getOperationColor,
  };
};

export default usePlotScaling;
